import globalNotification from 'live/services/global-notification'
import i18n from 'live/services/i18n'
import { AxiosError, AxiosInstance } from 'axios'

export interface RequestErrorOptions {
    showIncomingError?: boolean
    notify?: boolean
}

const get = <T>(axios: AxiosInstance): ((key: string, errorOptions?: RequestErrorOptions) => Promise<T>) => {
    return (key: string, errorOptions?: RequestErrorOptions): Promise<T> =>
        axios
            .get(key)
            .then(res => res.data)
            .catch((err: AxiosError<Record<string, string>>) => {
                const errorMessage = err.response.data.message

                ;(errorOptions?.notify || errorOptions?.showIncomingError) &&
                    // TODO: replace globalNOtification with beam
                    globalNotification.error(
                        errorOptions?.showIncomingError && errorMessage ? errorMessage : i18n('Error fetching data.')
                    )

                return Promise.reject(err)
            })
}

const post = <T, P = Record<string, unknown>>(axios: AxiosInstance) => {
    return (key: string, payload: P, errorOptions?: RequestErrorOptions): Promise<T> =>
        axios
            .post(key, payload)
            .then(res => res.data)
            .catch((err: AxiosError<Record<string, string>>) => {
                const errorMessage = err.response.data.message

                ;(errorOptions?.notify || errorOptions?.showIncomingError) &&
                    globalNotification.error(
                        errorOptions?.showIncomingError && errorMessage ? errorMessage : i18n('Error executing action')
                    )

                return Promise.reject(err)
            })
}

const put = <T, P = Record<string, unknown>>(axios: AxiosInstance) => {
    return (key: string, payload: P, errorOptions?: RequestErrorOptions): Promise<T> =>
        axios
            .put(key, payload)
            .then(res => res.data)
            .catch((err: AxiosError<Record<string, string>>) => {
                const errorMessage = err.response.data.message

                ;(errorOptions?.notify || errorOptions?.showIncomingError) &&
                    globalNotification.error(
                        errorOptions?.showIncomingError && errorMessage ? errorMessage : i18n('Error executing action')
                    )

                return Promise.reject(err)
            })
}

const _delete = <T, P = Record<string, unknown>>(axios: AxiosInstance) => {
    return (key: string, payload?: P, errorOptions?: RequestErrorOptions): Promise<T> =>
        axios
            .delete(key, { data: payload })
            .then(res => res.data)
            .catch((err: AxiosError<Record<string, string>>) => {
                const errorMessage = err.response.data.message

                ;(errorOptions?.notify || errorOptions?.showIncomingError) &&
                    globalNotification.error(
                        errorOptions?.showIncomingError && errorMessage ? errorMessage : i18n('Error executing action')
                    )

                return Promise.reject(err)
            })
}

const patch = <T, P = Record<string, unknown>>(axios: AxiosInstance) => {
    return (key: string, payload?: P, errorOptions?: RequestErrorOptions): Promise<T> =>
        axios
            .patch(key, payload)
            .then(res => res.data)
            .catch((err: AxiosError<Record<string, string>>) => {
                const errorMessage = err.response.data.message

                ;(errorOptions?.notify || errorOptions?.showIncomingError) &&
                    globalNotification.error(
                        errorOptions?.showIncomingError && errorMessage ? errorMessage : i18n('Error executing action')
                    )

                return Promise.reject(err)
            })
}

type Return = {
    get: <T>(key?: string, errorOptions?: RequestErrorOptions) => Promise<T>
    post: <T, P>(key: string, payload: P, errorOptions?: RequestErrorOptions) => Promise<T>
    put: <T, P>(key: string, payload: P, errorOptions?: RequestErrorOptions) => Promise<T>
    delete: <T, P>(key: string, payload?: P, errorOptions?: RequestErrorOptions) => Promise<T>
    patch: <T, P>(key: string, payload?: P, errorOptions?: RequestErrorOptions) => Promise<T>
}

export default (axios: AxiosInstance): Return => ({
    get: get(axios),
    post: post(axios),
    put: put(axios),
    delete: _delete(axios),
    patch: patch(axios)
})
